import { parseSignature } from 'viem'
import { ETH_COMISSION_WALLET, HYPERLIQUID_COMISSION } from '../constants/business'
import {
  ActivityItem,
  CandleProbe,
  CandleSnapshotParams,
  HyperLiquidApproveBuilderFeeRequest,
  HyperLiquidLeverageRequest,
  HyperLiquidOrder,
  HyperLiquidOrderRequest,
  HyperLiquidSpotSendRequest,
  HyperliquidUpdateLeverageAction,
  HyperLiquidUsdClassTransferRequest,
  HyperLiquidUsdSendRequest,
  HyperLiquidWithdrawRequest,
  LeverageParams,
  ModifyOrderParams,
  OrderParams,
  PerpAccountBalanceResponse,
  PerpetualMetaResponse,
  WithHyperliquidErorResponse,
} from '../interfaces/hyperliquid'
import { prepareUserSignedTypedData } from '../utils/hyperliquidSigning'

interface RequestParams {
  [key: string]: any
}

const API_URL = 'https://api.hyperliquid.xyz/'

const removeTrailingZeros = (value: string): string => {
  if (!value.includes('.')) return value

  const normalized = value.replace(/\.?0+$/, '')
  if (normalized === '-0') return '0'
  return normalized
}

class HyperLiquidClient {
  async #fetch<T>(path: string, body?: RequestParams) {
    const url = API_URL + path

    const options: RequestInit = { method: 'POST' }

    if (body) {
      options.headers = { 'Content-Type': 'application/json' }
      options.body = JSON.stringify(body)
    }

    const result = await fetch(url, options)

    if (result.headers.get('Content-Type') === 'application/json') {
      const response = (await result.json()) as T & WithHyperliquidErorResponse

      // User-signed actions (usdSend / spotSend / usdClassTransfer / withdraw) fail
      // with { status: 'err', response: '<message>' }; order/leverage actions report
      // per-item errors under response.data.statuses[].
      if (response.status === 'err') {
        throw Error(
          typeof response.response === 'string' ? response.response : 'Hyperliquid request failed'
        )
      }

      const statuses =
        typeof response.response === 'object' ? response.response?.data?.statuses : undefined
      const statusError = statuses?.find((status) => status?.error)?.error

      if (statusError) {
        throw Error(statusError)
      }

      return response
    } else {
      throw await result.text()
    }
  }

  #parseSig(signature: string) {
    const { r, s, v } = parseSignature(signature as `0x${string}`)
    return { r, s, v: Number(v) }
  }

  async getPerpetuals() {
    const response: PerpetualMetaResponse = await this.#fetch('info', { type: 'metaAndAssetCtxs' })
    return response[0].universe.map((token, id) => ({ ...token, id }))
  }

  async getAssetContext() {
    const response: PerpetualMetaResponse = await this.#fetch('info', { type: 'metaAndAssetCtxs' })
    return response[1]
  }

  async getCandleSnapshot(req: CandleSnapshotParams): Promise<CandleProbe[]> {
    return this.#fetch('info', { type: 'candleSnapshot', req })
  }

  #prepareOrder(
    assetId: number,
    price: string,
    size: string,
    isBuy: boolean,
    reduceOnly: boolean
  ): HyperLiquidOrder {
    return {
      type: 'order',
      orders: [
        {
          a: assetId,
          b: isBuy,
          p: removeTrailingZeros(price),
          s: removeTrailingZeros(size),
          r: reduceOnly,
          t: {
            limit: {
              tif: 'Ioc',
            },
          },
        },
      ],
      grouping: 'na',
      builder: {
        b: ETH_COMISSION_WALLET,
        f: HYPERLIQUID_COMISSION,
      },
    }
  }

  prepareBuyOrder({ assetId, price, size, type }: OrderParams): HyperLiquidOrder {
    return this.#prepareOrder(assetId, price, size, type === 'long', false)
  }

  prepareModifyOrder({
    currentOrder,
    reduce,
    size,
    price,
    assetId,
  }: ModifyOrderParams): HyperLiquidOrder {
    const currentOrderSize = parseFloat(currentOrder.position.szi)
    const isLong = currentOrderSize > 0
    const isBuy = (isLong && !reduce) || (!isLong && reduce)

    return this.#prepareOrder(
      assetId,
      price,
      reduce ? Math.min(Math.abs(currentOrderSize), Math.abs(parseFloat(size))).toString() : size,
      isBuy,
      reduce
    )
  }

  prepareLeverageUpdate({ asset, leverage }: LeverageParams): HyperliquidUpdateLeverageAction {
    return {
      type: 'updateLeverage',
      asset,
      isCross: false,
      leverage,
    }
  }

  prepareBuilderFeeApprove(builder: string, maxFeeRate: string) {
    const nonce = Date.now()

    const action = {
      type: 'approveBuilderFee' as const,
      hyperliquidChain: 'Mainnet' as const,
      signatureChainId: '0xa4b1' as `0x${string}`,
      maxFeeRate,
      builder,
      nonce,
    }

    const digest = prepareUserSignedTypedData({
      action,
      types: [
        { name: 'hyperliquidChain', type: 'string' },
        { name: 'maxFeeRate', type: 'string' },
        { name: 'builder', type: 'address' },
        { name: 'nonce', type: 'uint64' },
      ],
      primaryType: 'HyperliquidTransaction:ApproveBuilderFee',
    })

    return { action, digest, nonce }
  }

  prepareWithdraw(amount: string, destination: string) {
    const nonce = Date.now()

    const action = {
      type: 'withdraw3' as const,
      hyperliquidChain: 'Mainnet' as const,
      signatureChainId: '0xa4b1' as `0x${string}`,
      time: nonce,
      amount,
      destination,
    }

    const digest = prepareUserSignedTypedData({
      action,
      types: [
        { name: 'hyperliquidChain', type: 'string' },
        { name: 'destination', type: 'string' },
        { name: 'amount', type: 'string' },
        { name: 'time', type: 'uint64' },
      ],
      primaryType: 'HyperliquidTransaction:Withdraw',
    })

    return { action, digest, nonce }
  }

  prepareUsdSend(amount: string, destination: string) {
    const nonce = Date.now()

    const action = {
      type: 'usdSend' as const,
      hyperliquidChain: 'Mainnet' as const,
      signatureChainId: '0xa4b1' as `0x${string}`,
      destination,
      amount,
      time: nonce,
    }

    const digest = prepareUserSignedTypedData({
      action,
      types: [
        { name: 'hyperliquidChain', type: 'string' },
        { name: 'destination', type: 'string' },
        { name: 'amount', type: 'string' },
        { name: 'time', type: 'uint64' },
      ],
      primaryType: 'HyperliquidTransaction:UsdSend',
    })

    return { action, digest, nonce }
  }

  prepareSpotSend(amount: string, destination: string, token: string) {
    const nonce = Date.now()

    const action = {
      type: 'spotSend' as const,
      hyperliquidChain: 'Mainnet' as const,
      signatureChainId: '0xa4b1' as `0x${string}`,
      destination,
      token,
      amount,
      time: nonce,
    }

    const digest = prepareUserSignedTypedData({
      action,
      types: [
        { name: 'hyperliquidChain', type: 'string' },
        { name: 'destination', type: 'string' },
        { name: 'token', type: 'string' },
        { name: 'amount', type: 'string' },
        { name: 'time', type: 'uint64' },
      ],
      primaryType: 'HyperliquidTransaction:SpotSend',
    })

    return { action, digest, nonce }
  }

  prepareUsdClassTransfer(amount: string, toPerp: boolean) {
    const nonce = Date.now()

    const action = {
      type: 'usdClassTransfer' as const,
      hyperliquidChain: 'Mainnet' as const,
      signatureChainId: '0xa4b1' as `0x${string}`,
      amount,
      toPerp,
      nonce,
    }

    const digest = prepareUserSignedTypedData({
      action,
      types: [
        { name: 'hyperliquidChain', type: 'string' },
        { name: 'amount', type: 'string' },
        { name: 'toPerp', type: 'bool' },
        { name: 'nonce', type: 'uint64' },
      ],
      primaryType: 'HyperliquidTransaction:UsdClassTransfer',
    })

    return { action, digest, nonce }
  }

  async getPerpAccountBalance(address: string): Promise<PerpAccountBalanceResponse> {
    return this.#fetch('info', {
      type: 'clearinghouseState',
      user: address,
    })
  }

  async getActivity(address: string): Promise<ActivityItem[]> {
    return this.#fetch('info', {
      type: 'userFills',
      user: address,
    })
  }

  async executeExchangeAction(
    request:
      | HyperLiquidLeverageRequest
      | HyperLiquidOrderRequest
      | HyperLiquidApproveBuilderFeeRequest
      | HyperLiquidWithdrawRequest
      | HyperLiquidUsdSendRequest
      | HyperLiquidSpotSendRequest
      | HyperLiquidUsdClassTransferRequest
  ) {
    const response = await this.#fetch('exchange', {
      ...request,
      signature: this.#parseSig(request.signature),
    })
    return response
  }
}

export default HyperLiquidClient
